import { useMemo } from 'react';
import useAuth from './useAuth';
import { useCountry } from '../context/CountryContext';
import { operationAddressesOf, resolveOperationFraming } from '../utils/operationFraming';

/**
 * Encuadre inicial de los mapas operativos según dónde opera el cliente
 * autenticado: sus instalaciones de /admin/settings?tab=locations, y el centro de
 * su país como respaldo.
 *
 * Devuelve `null` mientras el dato no es confiable. Las vistas deben tratar ese
 * `null` como "todavía no encuadres", NO como "no hay encuadre": sembrar un centro
 * provisional y darlo por definitivo es exactamente el bug que esto arregla.
 *
 * POR QUÉ EL NULL. `useCountry()` reporta `countryCode: 'CL'` con `loading: true`
 * hasta que resuelve el catálogo de países, así que en una carga fría (F5 sobre el
 * mapa, o un kiosko que se abre por URL) un operador argentino recibiría Santiago
 * y —con un encuadre de una sola vez— se quedaría ahí toda la sesión.
 *
 * La espera solo aplica al respaldo por país: si el cliente declaró instalaciones
 * con coordenadas, el encuadre sale de ellas y el catálogo es irrelevante, así que
 * se resuelve de inmediato.
 *
 * @returns {{
 *   bounds: [[number, number], [number, number]] | null,
 *   center: [number, number],
 *   zoom: number,
 *   source: 'addresses' | 'country'
 * } | null}
 */
export default function useOperationFraming() {
  const { user } = useAuth();
  const { countryCode, loading } = useCountry();

  return useMemo(() => {
    const framing = resolveOperationFraming(operationAddressesOf(user), { countryCode });
    if (framing.source === 'addresses') return framing;
    return loading ? null : framing;
  }, [user, countryCode, loading]);
}
